'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';

import useEmblaCarousel from 'embla-carousel-react';
import { HiStar } from 'react-icons/hi';
import Link from 'next/link';

export default function Reviews() {
  const [data, setData] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState({});

  useEffect(() => {
    fetch('/api/reviews')
      .then((res) => res.json())
      .then((data) => {
        setData(data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  const options = { dragFree: true, loop: false };
  const [emblaRef, emblaApi] = useEmblaCarousel(options);

  if (!data) {
    return (
      <p className="text-gray-500 text-center">
        Caricamento recensioni in corso...
      </p>
    );
  }

  const toggleExpand = (index) => {
    setExpandedReviews((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  function renderReviewText(text, index) {
    const isExpanded = expandedReviews[index];
    const isLongText = text.length > 300;

    if (isLongText && !isExpanded) {
      const truncated = text.split(' ').splice(0, 35).join(' ');
      return (
        <>
          "{truncated}..."
          <button
            onClick={() => toggleExpand(index)}
            className="block text-sm font-bold text-primary-500/90 underline cursor-pointer underline-offset-2 mt-2 text-left">
            Leggi tutto
          </button>
        </>
      );
    }

    return (
      <>
        "{text}"
        {isLongText && (
          <button
            onClick={() => toggleExpand(index)}
            className="block text-sm font-bold text-primary-500/90 underline mt-2 underline-offset-4text-left cursor-pointer">
            Mostra meno
          </button>
        )}
      </>
    );
  }

  function convertToDate(date) {
    return new Date(date * 1000).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });
  }

  return (
    <section className="border border-black/10 pt-16 pb-0 md:pb-16 relative overflow-hidden bg-[url('/images/background.webp')] bg-no-repeat bg-cover bg-center shadow-xs shadow-black/10">
      <div className="embla flex md:flex-row flex-col lg:w-full md:w-[40%] w-full z-20 md:pt-0 pt-8 overflow-hidden md:ml-8 md:gap-0 gap-8">
        <div className="z-30 self-start flex flex-col justify-center md:w-fit w-[80%] mx-auto">
          <div className="flex flex-col justify-center items-center bg-white px-16 md:h-[350px] h-[300px] border border-black/10 rounded-xl md:mr-8">
            <div className="flex flex-row">
              {Array.from({ length: Math.round(data.rating) }, (_, i) => (
                <HiStar key={i} className="text-yellow-400 text-5xl" />
              ))}
            </div>
            <p className="text-ctext md:text-2xl text-xl mt-2">
              In base a{' '}
              <Link
                className="underline text-primary-900 text-xl underline-offset-2"
                href={
                  'https://www.google.com/search?sca_esv=05bf38ac0000406c&cs=1&sxsrf=ANbL-n7Q1vLDPqqBvxfreplZJ67QlluJ1A:1780428957862&uds=ALYpb_ncDc7jTlmw6Mmq7NjuX5c-UeBcSr9NZGfmgkmxDKmezwAEDdqhcBcSCVy_qF1kTQwiXf3gFbF4BVD8zsmJCI6oK-tPbSfqztavvZfS7iA3JYQvQDuNtI2br_ZPcOaueeAn-ciC-mvLkY7BnigdFRfX9gNlIAcSSt9a9WhRnLTsQUyoRJs&q=Fisioterapista+Angela+Formisano+Recensioni&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOYUfsim3-ufBWFdnZtLBLnzPDluKuAc2puYXETI58_aV_PPJkHbJYQMcs6u198SD5MfqlOyXABIIYvCvGPQLChIbBD7WNo-JiRkosxshUPN8suKPF0SLmXjKRaBMHpJB0Cegffc%3D&hl=it-IT&sa=X&ved=2ahUKEwizmdHcpumUAxVXS0EAHezsJAYQ_4MLegQIEBAM&biw=2048&bih=1026&dpr=1.25'
                }>
                {data.user_ratings_total} recensioni
              </Link>
            </p>
            <Image
              src={'/images/google_logo.webp'}
              alt="Google logo"
              width={200}
              height={200}
              className="mt-4"
            />
          </div>
        </div>

        <div
          className="max-w-[100vw] w-[80%] mx-auto overflow-hidden"
          ref={emblaRef}>
          <div className="gap-4 embla__container pl-4 md:pr-16 items-start">
            {data?.reviews?.map((review, i) => (
              <div
                key={i}
                className="embla__slide h-full relative flex flex-col bg-white/50 backdrop-blur-sm p-6 flex-none lg:basis-1/3 basis-full rounded-xl border border-black/10 self-start transition-all duration-300">
                <div>
                  <div className="flex flex-row gap-2">
                    <div className="flex items-center">
                      <Image
                        src={review.profile_photo_url}
                        width={40}
                        height={40}
                        alt="Reviewer propic"
                        className="rounded-full"
                      />
                    </div>
                    <div className="flex flex-col">
                      <h3 className="text-2xl tracking-tight font-lato font-bold text-ctext">
                        {review.author_name}
                      </h3>
                      <span className="text-ctext/75 text-sm font-lato font-semibold">
                        {convertToDate(review.time)}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col justify-start pt-2 transition-all duration-300">
                    <div className="flex flex-row text-xl">
                      {Array.from({ length: review.rating }, (_, i) => (
                        <HiStar key={i} className="text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-ctext text-lg leading-relaxed font-alice italic pt-4 pr-4 transition-all duration-300">
                      {renderReviewText(review.text, i)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
